import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      // Ink colours (cmp-*), as in button.tsx: the shadcn names aren't Tailwind
      // colours here, and text-white is remapped to ink.
      variant: {
        default: 'border-transparent bg-primary text-cmp-onink [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-cmp-surface text-cmp-ink-2 [a&]:hover:bg-cmp-line',
        destructive:
          'border-transparent bg-cmp-crit text-cmp-onink [a&]:hover:bg-cmp-crit/90 focus-visible:ring-cmp-crit/20',
        outline: 'border-cmp-line-2 text-cmp-ink-2 [a&]:hover:bg-cmp-surface',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
